import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { SavedReport } from "@/entities/savedReports/types";
import { useRenameReport } from "@/entities/savedReports/hooks";
import { TRANSLATION_KEY } from "@/components/global/Chat/components/dreamerReports/consts";
import { useChat } from "@/components/layouts/Chat/ChatContext";
import { useReportRuns } from "@/components/global/Chat/components/dreamerReports/ReportRunsContext";

export const MIN_REPORT_NAME_LENGTH = 1;
export const MAX_REPORT_NAME_LENGTH = 256;

interface UseReportRenameParams {
  report: Pick<SavedReport, "id" | "name">;
  isRenaming: boolean;
  onFinish: () => void;
}

/**
 * Inline rename of a report, shared by the panel row and the expanded table row
 * so the two surfaces commit, validate and roll back identically.
 *
 * Whether the row is being renamed is owned by the caller: the panel row keeps
 * its own flag, while the table keeps one rename target for the whole table so
 * that opening a second rename closes the first.
 */
export const useReportRename = ({
  report,
  isRenaming,
  onFinish
}: UseReportRenameParams) => {
  const { t } = useTranslation();
  const [draftName, setDraftName] = useState(report.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const renameReport = useRenameReport();
  const { renameSavedReport } = useChat();
  const { renameReportRun } = useReportRuns();

  // Read through a ref so the effect runs when the rename opens and not when
  // the name changes: renaming another report, or any schedule change,
  // refetches the list, and depending on the name would wipe what the user is
  // typing on another row mid-edit.
  // Written after commit, not during render: a render React discards would
  // otherwise leave the ref holding a name the committed tree never had, and
  // the effect below would seed the draft from it.
  const reportNameRef = useRef(report.name);
  useEffect(() => {
    reportNameRef.current = report.name;
  }, [report.name]);

  useEffect(() => {
    if (!isRenaming) return;
    setDraftName(reportNameRef.current);
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isRenaming]);

  const commitRename = useCallback(() => {
    if (!isRenaming) return;
    onFinish();
    const trimmedName = draftName.trim();
    if (
      trimmedName.length < MIN_REPORT_NAME_LENGTH ||
      trimmedName.length > MAX_REPORT_NAME_LENGTH ||
      trimmedName === report.name
    ) {
      setDraftName(report.name);
      return;
    }
    renameReport.mutate(
      {
        pathParams: { id: report.id },
        data: { name: trimmedName }
      },
      {
        onSuccess: (renamed) => {
          renameSavedReport(report.id, renamed.name);
          renameReportRun(report.id, renamed.name);
        },
        onError: () => {
          setDraftName(report.name);
          toast.error(t(`${TRANSLATION_KEY}.rename.failed`));
        }
      }
    );
  }, [
    isRenaming,
    onFinish,
    draftName,
    report.name,
    report.id,
    renameReport,
    renameSavedReport,
    renameReportRun,
    t
  ]);

  const cancelRename = useCallback(() => {
    onFinish();
    setDraftName(report.name);
  }, [onFinish, report.name]);

  return { draftName, setDraftName, inputRef, commitRename, cancelRename };
};
